import Phaser from 'phaser';
import type { InputFrame, MoveAxis } from '../player/stateMachine';
import { EdgeCounter } from './edgeCounter';

const K = Phaser.Input.Keyboard.KeyCodes;

/** Gameplay bindings. Jump aliases: Space, W and ↑. */
export const CONTROL_KEYS = {
  left: [K.A, K.LEFT],
  right: [K.D, K.RIGHT],
  run: [K.SHIFT],
  jump: [K.SPACE, K.W, K.UP],
} as const;

type Key = Phaser.Input.Keyboard.Key;

/** A tracked Key plus its replay-proof press counter (see edgeCounter.ts). */
interface EdgeKey {
  key: Key;
  edges: EdgeCounter;
}

function trackEdges(keyboard: Phaser.Input.Keyboard.KeyboardPlugin, code: number): EdgeKey {
  const key = keyboard.addKey(code, true);
  const edges = new EdgeCounter();
  key.on(Phaser.Input.Keyboard.Events.DOWN, (_key: Key, event: KeyboardEvent) =>
    edges.record(event),
  );
  return { key, edges };
}

/**
 * Keyboard input through tracked Key objects only — never `keyboard.on('keydown-X')`. Held keys
 * read `isDown`; edges (jump) count distinct key-down events and are consumed once per frame,
 * which survives both Phaser's queue replay and same-frame taps (edgeCounter.ts explains why
 * neither raw handlers nor `JustDown` do).
 */
export class KeyboardControls {
  private readonly left: Key[];
  private readonly right: Key[];
  private readonly run: Key[];
  private readonly jump: EdgeKey[];

  constructor(keyboard: Phaser.Input.Keyboard.KeyboardPlugin) {
    const add = (codes: readonly number[]) => codes.map((code) => keyboard.addKey(code, true));
    this.left = add(CONTROL_KEYS.left);
    this.right = add(CONTROL_KEYS.right);
    this.run = add(CONTROL_KEYS.run);
    this.jump = CONTROL_KEYS.jump.map((code) => trackEdges(keyboard, code));
  }

  /** This tick's input; consumes the jump edges. Call exactly once per update. */
  snapshot(): InputFrame {
    const left = this.left.some((k) => k.isDown);
    const right = this.right.some((k) => k.isDown);
    const moveAxis: MoveAxis = left === right ? 0 : left ? -1 : 1;
    let jumpPressed = false;
    for (const { edges } of this.jump) if (edges.consume() > 0) jumpPressed = true;
    return { moveAxis, runHeld: this.run.some((k) => k.isDown), jumpPressed };
  }

  /** Forget held keys and pending edges (scene switch). */
  release(): void {
    for (const key of [...this.left, ...this.right, ...this.run]) key.reset();
    for (const { key, edges } of this.jump) {
      key.reset();
      edges.reset();
    }
  }
}

/**
 * Edge-triggered command keys (toggles, respawn, scene switch): `pressed()` returns the names
 * pressed since the previous poll — call it exactly once per frame.
 */
export class EdgeKeys<Name extends string> {
  private readonly keys: [Name, EdgeKey][];

  constructor(keyboard: Phaser.Input.Keyboard.KeyboardPlugin, bindings: Record<Name, number>) {
    this.keys = (Object.entries(bindings) as [Name, number][]).map(([name, code]) => [
      name,
      trackEdges(keyboard, code),
    ]);
  }

  pressed(): Set<Name> {
    const out = new Set<Name>();
    for (const [name, { edges }] of this.keys) if (edges.consume() > 0) out.add(name);
    return out;
  }
}
